import type { LocalAutomationScheduleKind } from "./localAutomationClient";

export type AutomationTemplateCategory = "Engineering" | "Operations" | "Personal";
export type AutomationTemplateCategoryFilter = "All" | AutomationTemplateCategory;
export type AutomationTemplateRisk = "Low" | "Medium" | "High";

export interface AutomationTemplateSchedule {
  readonly kind: Exclude<LocalAutomationScheduleKind, "every_minute">;
  readonly time: string | null;
  readonly dayOfWeek: number | null;
}

export interface AutomationTemplateDefinition {
  readonly key: string;
  readonly name: string;
  readonly category: AutomationTemplateCategory;
  readonly risk: AutomationTemplateRisk;
  readonly description: string;
  readonly summary: string;
  readonly connectors: readonly string[];
  readonly prompt: string;
  readonly schedule: AutomationTemplateSchedule;
  readonly target: "project" | "none";
}

export const AUTOMATION_TEMPLATE_CATEGORIES: readonly AutomationTemplateCategoryFilter[] = [
  "All",
  "Engineering",
  "Operations",
  "Personal",
];

export const AUTOMATION_TEMPLATES: readonly AutomationTemplateDefinition[] = [
  {
    key: "pr_babysitter",
    name: "PR Babysitter",
    category: "Engineering",
    risk: "Medium",
    description:
      "Keep an active pull request moving by checking CI, review comments, and merge conflicts.",
    summary: "Keep active pull requests moving.",
    connectors: ["GitHub"],
    schedule: { kind: "daily", time: "09:00", dayOfWeek: null },
    target: "project",
    prompt:
      "Review active pull requests for failing checks, unresolved review feedback, and merge conflicts. Address the highest-priority actionable issue with the smallest correct change, run focused verification, and update the pull request. Do not merge. If nothing needs action, leave a concise status summary.",
  },
  {
    key: "daily_engineering_brief",
    name: "Daily Engineering Brief",
    category: "Engineering",
    risk: "Low",
    description:
      "Start the day with the important pull requests, bugs, failures, and repository risks.",
    summary: "Surface blockers, failures, and risks.",
    connectors: ["GitHub", "CMMD"],
    schedule: { kind: "daily", time: "08:30", dayOfWeek: null },
    target: "none",
    prompt:
      "Prepare a short engineering brief covering meaningful changes since the previous run. Prioritize blocked pull requests, new high-impact bugs, failing workflows, dependency or security alerts, and work that needs a decision. Link every item to its source and omit routine noise.",
  },
  {
    key: "ci_failure_investigator",
    name: "CI Failure Investigator",
    category: "Engineering",
    risk: "Medium",
    description: "Find the highest-signal failed workflow, diagnose it, and prepare a focused fix.",
    summary: "Diagnose one high-signal CI failure.",
    connectors: ["GitHub"],
    schedule: { kind: "daily", time: "09:00", dayOfWeek: null },
    target: "project",
    prompt:
      "Inspect recent failed CI workflows and select the highest-signal failure that is not already being handled. Reproduce or trace the failure, identify the root cause, implement the minimal correct fix, run the relevant checks, and prepare or update a pull request. Do not hide flaky failures or weaken test coverage.",
  },
  {
    key: "dependency_security_maintenance",
    name: "Dependency & Security Maintenance",
    category: "Engineering",
    risk: "Medium",
    description: "Review advisories and dependency updates, then land one safe improvement.",
    summary: "Ship one safe, valuable update.",
    connectors: ["GitHub"],
    schedule: { kind: "weekly", time: "09:00", dayOfWeek: 1 },
    target: "project",
    prompt:
      "Review dependency updates, security advisories, and deprecated packages. Select one safe high-value update with a clear reason, implement it without unrelated upgrades, run focused verification, and prepare a pull request. Escalate breaking or ambiguous updates instead of guessing.",
  },
  {
    key: "test_coverage_gaps",
    name: "Test Coverage Gaps",
    category: "Engineering",
    risk: "Low",
    description: "Add focused tests where recently changed behavior carries the most risk.",
    summary: "Cover the riskiest untested behavior.",
    connectors: ["GitHub"],
    schedule: { kind: "weekly", time: "09:00", dayOfWeek: 2 },
    target: "project",
    prompt:
      "Review recently merged behavior for important untested paths, regressions, and error handling. Choose a small high-value coverage gap, add focused tests that follow repository conventions, and run the relevant test target. Do not change production behavior unless a real defect is discovered and clearly reported.",
  },
  {
    key: "release_readiness",
    name: "Release Readiness",
    category: "Operations",
    risk: "Medium",
    description:
      "Check blockers, migrations, documentation, and deployment health before a release.",
    summary: "Find blockers before the release cut.",
    connectors: ["GitHub", "Railway"],
    schedule: { kind: "weekly", time: "09:00", dayOfWeek: 5 },
    target: "project",
    prompt:
      "Assess release readiness using the repository, open pull requests, recent deployments, migrations, and release notes. Report blockers by severity with evidence and owners. Fix only small, clearly authorized issues; otherwise produce an actionable checklist. Never deploy or publish a release without explicit approval.",
  },
  {
    key: "production_error_triage",
    name: "Production Error Triage",
    category: "Operations",
    risk: "High",
    description:
      "Investigate recent production errors and prepare a reviewed fix when evidence is strong.",
    summary: "Triage the highest-impact new error.",
    connectors: ["Sentry", "GitHub"],
    schedule: { kind: "daily", time: "09:00", dayOfWeek: null },
    target: "project",
    prompt:
      "Review new and regressed production errors since the previous run. Group duplicates, identify the highest-impact actionable issue, inspect relevant traces and code changes, and propose the root cause. Prepare a minimal fix and focused tests only when evidence is sufficient. Never deploy automatically.",
  },
  {
    key: "weekly_codebase_health",
    name: "Weekly Codebase Health",
    category: "Engineering",
    risk: "Low",
    description: "Find recurring failures, risky changes, and small reliability improvements.",
    summary: "Surface recurring reliability risks.",
    connectors: ["GitHub", "CMMD"],
    schedule: { kind: "weekly", time: "09:00", dayOfWeek: 1 },
    target: "project",
    prompt:
      "Review the last week of repository activity. Summarize risky changes, recurring failures, stale bugs, dependency concerns, and small high-leverage reliability improvements. Open a pull request only for a clearly scoped, low-risk fix with focused verification.",
  },
  {
    key: "morning_briefing",
    name: "Morning Briefing",
    category: "Personal",
    risk: "Low",
    description:
      "Prioritize messages, meetings, assignments, and decisions needing attention today.",
    summary: "Prioritize today’s messages and decisions.",
    connectors: ["Slack", "Calendar", "CMMD"],
    schedule: { kind: "daily", time: "08:00", dayOfWeek: null },
    target: "none",
    prompt:
      "Prepare my morning briefing from the previous 24 hours. Summarize urgent messages, today's meetings, assigned work, blocked decisions, and deadlines. Deduplicate the same topic across sources, rank items by urgency and impact, and include source links. Do not send messages or change records.",
  },
  {
    key: "recurring_research_watch",
    name: "Recurring Research Watch",
    category: "Personal",
    risk: "Low",
    description: "Track chosen competitors or technical topics and report meaningful changes.",
    summary: "Report meaningful changes with sources.",
    connectors: ["Web"],
    schedule: { kind: "weekly", time: "09:00", dayOfWeek: 5 },
    target: "none",
    prompt:
      "Research the tracked topics since the previous run using authoritative sources. Report only meaningful new information, explain why it matters, link every claim to a source, and separate confirmed facts from inference. Avoid repeating unchanged background.",
  },
];

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatTime(time: string | null): string {
  if (!time) return "9:00 AM";
  const [hourValue = "9", minute = "00"] = time.split(":");
  const hour = Number(hourValue);
  const meridiem = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${meridiem}`;
}

export function formatTemplateSchedule(schedule: AutomationTemplateSchedule): string {
  if (schedule.kind === "manual") return "Manual";
  if (schedule.kind === "daily") return `Daily · ${formatTime(schedule.time)}`;
  return `${WEEKDAYS[schedule.dayOfWeek ?? 1] ?? "Monday"} · ${formatTime(schedule.time)}`;
}

export function filterAutomationTemplates(
  templates: readonly AutomationTemplateDefinition[],
  category: AutomationTemplateCategoryFilter,
  query: string,
): readonly AutomationTemplateDefinition[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  return templates.filter((template) => {
    const matchesCategory = category === "All" || template.category === category;
    if (!matchesCategory) return false;
    if (!normalizedQuery) return true;
    return [
      template.name,
      template.summary,
      template.description,
      template.category,
      ...template.connectors,
    ]
      .join(" ")
      .toLocaleLowerCase()
      .includes(normalizedQuery);
  });
}
